import React, { useEffect } from 'react'

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy - Flux App'
  }, [])

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#333', lineHeight: 1.8 }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Privacy Policy for Flux App</h1>
      <p style={{ color: '#888', marginBottom: 32, textAlign: 'left' }}>Last updated: </p>

      <p>Thank you for using Flux App.</p>
      <p>
        Hangzhou Furui Technology Co., Ltd and their affiliates ("Flux", "we", "our", "us") is committed to protecting the privacy,
        confidentiality and security of your personal information by complying with applicable laws, and we are equally committed to
        ensuring that all our employees and agents uphold these obligations.
      </p>
      <p>This Privacy Policy describes how we collect, use and handle your personal information when you use our Flux App.</p>
      <p>
        "Flux Services" via Flux App may include (1) software applications that can be downloaded to a mobile device, namely this Flux App;
        and (2) any services available on this App, including Flux Plus Service and other available services. You may need to register an
        Account via Flux App to access our Services. For the purposes of seeking to provide you with a better experience, to improve Flux
        Services or otherwise where you have consented, with such Account without new registration, you may access to the services provided
        by us over Internet and/or mobile applications. You may also from time to time ask us to migrate or export your personal information
        contained in one service to another services, where such migration option is available. Any applicable privacy policy may apply to
        specific services provided by Flux, you are advised to read this Privacy Policy and any specific policy for your interest before
        using the services.
      </p>
      <p>
        Flux Services may contain links to other websites or online services that are operated and maintained by third parties and that are
        not under the control of or maintained by us. This Privacy Policy does not apply to how third parties collect, use, disclose or
        retain information. We encourage you to review the privacy policies of these third-party websites or services.
      </p>
      <p>
        We may modify this Privacy Policy at any time, provided certain provisions of this Privacy Policy prove to be incomplete or outdated
        and further provided that these changes are reasonable for you, taking into account your interests. We will post the most current
        version on the page of Flux App-Log in-More-About-Privacy Policy. If we make material changes to this Privacy Policy, we will notify
        you by the email address specified in your account or by means of notice on the Flux app of any such changes. By using Flux Services
        or other acting permitted by the applicable laws, you are deemed to have read, acknowledged and accepted all the provisions stated
        here in this Privacy Policy, including any changes we may make from time to time. In order to comply with applicable laws, including
        local data protection legislation (e.g. California Privacy Protection Act), we will specifically seek prior express consent to the
        particular processing (e.g. providing marketing materials to you) of personal information. Furthermore, we are committed to
        protecting the privacy, confidentiality and security of your personal information by complying with applicable laws, and we are
        equally committed to ensuring that all our employees and agents uphold these obligations.
      </p>
      <p>You can determine when this Privacy Policy was last revised by referring to the date it was "Last Updated" above.</p>

      <h2 style={sectionTitle}>1. Definitions</h2>
      <p>
        "Personal Information" shall have the same meaning as "personal data" and shall include any information relating to an identified or
        identifiable natural person ("data subject"); an identifiable person is one who can be identified, directly or indirectly, in
        particular by reference to an identification number or to one or more factors specific to his physical, physiological, mental,
        economic, cultural or social identity. Examples of personal information include your name, telephone number, email address, physical
        address, and payment information.
      </p>
      <p>
        Personal information also includes information that alone cannot directly identify you, but with other information we have access to
        can identify you, such as log data that automatically records information about your visit, such as your browser type, domains, page
        views, the URL of the page that referred you, the URL of the page you next visit, your IP address, and page navigation, unique
        device ID collected from your mobile devices, data from cookies, pixel tags, and web beacons, geo-location, mobile phone carrier
        identification.
      </p>

      <h2 style={sectionTitle}>2. The Type of Personal Information We Process and Why We Collect it</h2>
      <p>
        In order to provide the Services of Flux App to you, as a controller, we collect and process your Personal Information. We will ask
        you to provide Personal Information that is necessary to provide those services to you, and we process the Personal Information which
        you voluntarily provide to us. If you do not provide your Personal Information, we may not be able to provide you with our Flux
        Service.
      </p>
      <p>Here are more details on the types of Personal Information we collect when you use Flux Service:</p>

      <h3 style={subTitle}>Account information</h3>
      <p>
        We collect, and associate with your account, the information you provide to us when you do things such as install and use Flux App
        on your mobile device, sign up for your account, subscribe for premium services (e.g. Flux Plus Service), and set up two-factor
        authentication, e.g. your user name, profile photo or payment information. When you sign up for your account, we will also collect
        your location, e.g. which country or district you resident. We use this location to decide the nearest data center to support your
        use of Flux Service, and for applicable taxation compliance in your jurisdictions.
      </p>

      <h3 style={subTitle}>Your Content</h3>
      <p>
        Our Services enable you to view, save and share content through the Services, e.g. Your Input and the Output you received ("Your
        Content"). To make that possible, we may record, process and transmit them, when you use the Service's features.
      </p>

      <h3 style={subTitle}>Customer service information</h3>
      <p>
        If you contact us with questions or concerns about Flux Services, we may use your personal information to verify your identity and
        respond. When there are reasonable doubts regarding your identity, you might be asked to provide further information, e.g. a copy of
        your device ID, which help us to verify your identity. Should you provide any other documents, personal details such as your name
        and your physical address should be in clear in order to be able to identify you, while any other data such as a photo or any
        personal characteristics, may be blacked out. Our use of the information on your identification document is strictly limited: the
        information will only be used to verify your identity and will not be stored for longer than needed for this purpose.
      </p>

      <h3 style={subTitle}>Usage information</h3>
      <p>
        When you access Flux Services, we will collect information related to how you use Flux Services, such as the types of content that
        you view or engage with, the features you use, the actions you take, and the time, frequency and duration of your activities. We use
        this information to provide you with a more friendly experience, to prevent misuse, and to ensure that our Services are working
        properly. For example, to improve Flux Services, we collect information about how you interact with Flux Services to understand what
        features are most useful to you and improve them; to protect our users, we analyze things like IP addresses, device type, login
        history, and password changes to detect and respond to abusive behavior.
      </p>

      <h3 style={subTitle}>Device information</h3>
      <p>
        We also collect information from and about the devices you use to access Flux Service. This includes things like the browser, device
        name, type of network and Operator you use, and the UUID, model, system version, language and time zone of your device. We may also
        receive information through device settings that you turn on, such as access to your GPS location. If you have questions about the
        security and privacy settings of your mobile device, please refer to instructions from your mobile service provider or the
        manufacturer of your device to learn how to adjust your settings.
      </p>

      <h3 style={subTitle}>Cookies and other technologies</h3>
      <p>
        We also use cookies, web beacons, pixel tags, and other technologies to keep records, store your preferences, improve our
        advertising, and collect information such as log data and device data. For example, cookies help us with things like remembering your
        username for your next visit, understanding how you are interacting with our Flux Service, and improving them based on that
        information. This allows us to better understand how you use our Flux Service, diagnose and troubleshoot any problems you have, and
        otherwise administer and improve our Flux Service.
      </p>

      <h3 style={subTitle}>For Premium Service options</h3>
      <p>
        We may provide some software services that support separate subscription plan ("Premium Services"), which could collect other
        information. If you subscribe for Premium Services, e.g. Flux Plus Service, we may collect and process payment information, including
        your name, credit or debit card information, billing address, and details of your transaction history.
      </p>

      <h2 style={sectionTitle}>3. How We Use Your Personal Information</h2>
      <p>
        We process your Personal Information as a controller: (1) to provide Flux Service described in Terms of Services; (2) in furtherance
        of our legitimate interests in operating Flux Service and business; and (3) with your consent. In some cases, we may process your
        information to comply with applicable law, legal process, or regulation, e.g. meeting a legal obligation, a court order, official
        request or other binding decision(s), such as sharing data with law enforcement or tax authorities, or to carry out a task in the
        public interest.
      </p>
      <p><strong>(1) Examples of Flux processing your personal information to provide you with Flux Service include:</strong></p>
      <ul style={listStyle}>
        <li>Providing, processing, updating, maintaining, and developing Flux Service.</li>
        <li>Providing support and responding to your requests and enquiries.</li>
        <li>Troubleshooting issues you may encounter with Flux Service.</li>
        <li>Sending you reminders, technical notices, updates, alerts, support and administrative messages, service bulletins, and requested information. These communications are considered part of Flux Service and you may not opt out of them.</li>
      </ul>
      <p><strong>(2) Examples of Flux processing your information in furtherance of our legitimate interests in operating Flux Service and business include:</strong></p>
      <ul style={listStyle}>
        <li>For fraud prevention and detection.</li>
        <li>Increasing the safety of Flux Service – for example, for user authentication, security protection, fraud detection, filing, and backups.</li>
        <li>Analyzing and developing statistical information about the use of Flux Service to better improve Flux Service.</li>
        <li>Enhancing other information we have about you to help us better understand you and determine your interests.</li>
        <li>Protecting our rights or property, enforcing our Terms of Service and legal notices and for the establishment, exercise and defense of legal claims.</li>
        <li>Managing our everyday business needs, such as auditing, administration of Flux Service, forum management, fulfilment, analytics, fraud prevention, and enforcement of our corporate reporting obligations and Terms of Service.</li>
      </ul>
      <p><strong>(3) Examples of Flux processing your information with your consent include:</strong></p>
      <ul style={listStyle}>
        <li>Contacting to tell you about our available Services as well as other promotions, which we believe may interest you unless you advise us that you do not wish to receive marketing or market research communications from us or unless you have not given us your consent in line with Applicable Data Protection laws.</li>
        <li>Collecting feedback or answers to our questionnaires from you to improve our Services, and develop new features.</li>
      </ul>

      <h2 style={sectionTitle}>4. How We Share Your Personal Information</h2>
      <p>We do not sell any personal information to third parties and we never will.</p>
      <p>We may share personal information with third parties on the basis of non-disclosure agreements. The following are the limited situations where we may share personal information:</p>

      <h3 style={subTitle}>Our Affiliates</h3>
      <p>We share your personal information with Flux affiliates, who are required to use that information in accordance with the purposes described in this Privacy Policy, for example, to provide technical support as per your request.</p>

      <h3 style={subTitle}>Others working for and with Flux</h3>
      <p>
        Flux uses service providers, vendors, technicians and other third-parties to help us process, store, and protect some of your data
        and otherwise help us administer our Flux Service effectively, provide a better user experience, process your purchases, and increase
        the quality of our Flux Service. These third-parties are forbidden from using your personal information for non-Flux purposes and are
        required to protect your information in accordance with this Privacy Policy and applicable laws. For a list of trusted third parties
        that we use to process your personal information, please contact us.
      </p>

      <h3 style={subTitle}>New Owner</h3>
      <p>
        If Flux and/or all or part of our assets are ever sold or transferred, your personal information may be among the items sold or
        transferred. Under such circumstance, we will notify you by sending an email to the email address associated with your account or
        via an in-app or website notification of (i) the identity and contact information of the purchaser or transferee, (ii) your right to
        revoke your consent to the provision of personal information, and (iii) the means by which you may revoke such consent.
      </p>

      <h3 style={subTitle}>Law and Order; the Public Interest</h3>
      <p>
        We may provide information to third-parties if we believe in good faith that we are required by mandatory law to do so. For example,
        to comply with legal orders and government requests; response to a subpoena, or similar legal process, including to law enforcement
        agencies, regulators, and courts; to protect the interests of our customers and users of Flux Service; to respond to claims that any
        content posted or displayed using Flux Service violates the rights of third parties; or to enforce compliance with our Terms of
        Service.
      </p>

      <h3 style={subTitle}>Legitimate Business Interests of Flux</h3>
      <p>
        We share information to protect our own legitimate business interests when we believe in good faith that we are required or permitted
        by law to do so. For example, we may share your personal information as needed to support auditing, compliance, and corporate
        governance functions; to combat fraud or criminal activity; to protect our rights or those of our affiliates and users; or as part of
        legal proceedings affecting Flux.
      </p>
      <p>
        In addition to the above, we may also disclose non-personal information (for example, aggregated or anonymized data) publicly or
        with third-parties, provided those data have been rendered anonymous in such a way that the data subject is no longer identifiable.
        For example, we may share non-personal information:
      </p>
      <ul style={listStyle}>
        <li>for the same reasons we might share Personal information;</li>
        <li>to better understand how our customers interact with Flux Service, in order to optimize your experience, or provide better services;</li>
        <li>for our own research and data analytics; or,</li>
        <li>to our vendors for their own analysis and research.</li>
      </ul>

      <h2 style={sectionTitle}>5. Securing Your Personal Information</h2>
      <p>
        Flux has implemented commercially reasonable administrative, technical, and physical security controls that are designed to safeguard
        personal information. We have a team dedicated to keeping your information secure and testing for vulnerabilities. We conduct periodic
        reviews and assessments of the effectiveness of our security controls. We also continue to work on features to keep your information
        safe in addition to things like two-factor authentication.
      </p>
      <p>
        Notwithstanding the above, no method of transmission over the Internet, or method of electronic storage, is 100% secure. Therefore,
        Flux cannot guarantee that your information is under absolute security with the existing security technology. However, we will handle
        personal data breaches as required by applicable data protection law which includes, where required, notifying the breach to the
        relevant data protection supervisory authority and data subjects.
      </p>
      <p>
        If you have any further questions about the security of our Flux Service, you can easily contact us by email at{' '}
        <a href="mailto:appworks.ai@gmail.com" style={linkStyle}>appworks.ai@gmail.com</a>.
      </p>

      <h2 style={sectionTitle}>6. Your Rights and Retention of Your Personal Information</h2>
      <p>
        Generally, we store your information on Flux's servers which is established upon third-party servers, until you delete or edit it, or
        for as long as you remain a Flux user in order to provide you with the most relevant offers. Please contact us if you would like more
        information about our retention policies.
      </p>
      <p>
        Keeping your personal information current helps ensure that we provide you with the most relevant offers. You can access, update or
        delete your personal information by logging into your Flux Account and going to your account setting page. We are ready to assist you
        in managing your subscriptions, deactivating your account, and removing your active profile and data. Your personal information might
        not be immediately deleted, as we are required to retain records relating to previous purchases through Flux Service for financial
        reporting and compliance reasons pursuant to applicable laws. In addition, because of the way we maintain certain services, after you
        delete certain information, we may temporarily retain backup copies of such information before it is permanently deleted.
      </p>
      <p>
        We will retain your personal information for the period necessary to fulfill the purpose outlined in this Privacy Policy unless a
        longer retention period is required or permitted by applicable law.
      </p>

      <h2 style={sectionTitle}>7. Links to other websites</h2>
      <p>
        We may permit others to link to Flux Service or to post a link to their website. We do not endorse these websites and are not
        responsible for other websites or their privacy practices. Please read their privacy policies before submitting information.
      </p>

      <h2 style={sectionTitle}>8. Your Choices</h2>
      <p>
        We think that you benefit from a more personalized experience when we know more about you and your preferences. However, you can
        limit the information you provide to Flux as well as the communications you receive from Flux through your Account or usage
        preferences.
      </p>
      <h3 style={subTitle}>Commercial E-mails</h3>
      <p>
        You will only receive commercial e-mails from us if you have granted prior express consent or if sending those e-mails is otherwise
        permitted, in accordance with applicable laws. You may choose not to receive commercial e-mails from us by following the instructions
        contained in any of the commercial e-mails we send or by logging into your Account and adjusting your e-mail preferences. Please note
        that even if you unsubscribe from commercial email messages, we may still e-mail you non-commercial e-mails related to your Account
        and your transactions on Flux Service.
      </p>
      <h3 style={subTitle}>Device Data</h3>
      <p>
        You may manage how your mobile device and mobile browser share certain device data with Flux by adjusting the privacy and security
        settings on your mobile device. Please refer to instructions provided by your mobile service provider or the manufacturer of your
        device to learn how to adjust your settings.
      </p>

      <h2 style={sectionTitle}>9. Children's Privacy</h2>
      <p>
        We do not intend that any portion of its Flux Service will be accessed or used by children under the age of 18, or equivalent minimum
        age in the relevant jurisdiction and such use is prohibited. Our Flux Services are designed and intended for adults. By using Flux
        Service, you represent that you are at least 18 years old, or above the equivalent minimum age in the relevant jurisdiction and
        understand that you must be at least 18 years old, or above the equivalent minimum age in the relevant jurisdiction in order to create
        an account and purchase the services advertised through our Flux Service. If we obtain actual knowledge that an account is associated
        with a registered user who is under the age of 18 years old, or equivalent minimum age in the relevant jurisdiction, we will promptly
        delete information associated with that account. If you are a parent or guardian of a child under the age of 18, or equivalent minimum
        age in the relevant jurisdiction and believe he or she has disclosed personal information to us, please contact us by email at{' '}
        <a href="mailto:appworks.ai@gmail.com" style={linkStyle}>appworks.ai@gmail.com</a>. A parent or guardian of a child under the age of
        18, or equivalent minimum age in the relevant jurisdiction may review and request deletion of such child's personal information as
        well as prohibit the use thereof.
      </p>

      <h2 style={sectionTitle}>10. Global Operations and Data Transfers</h2>
      <p>
        We transfer and process your information globally both in our own facilities and with service providers, or partners, regardless of
        where you use our Services. As Flux operates globally, we may transfer your personal information to our affiliates worldwide, when
        processing that information for the purposes described in this Privacy Policy, e.g. if you request for customer services from Flux,
        our colleagues in China may access certain of your information (wherever you are located) in order to provide the requested services
        to you. We shall do so in accordance with the applicable laws. In particular, we will ensure that all transfers will be in accordance
        with requirements under your applicable local data protection laws by putting in place appropriate safeguards. You hereby agree to the
        data transfer and you have the right to be informed of the appropriate safeguards taken by Flux for this transfer of your Personal
        Information.
      </p>
      <p>
        Currently we have data centers in the U.S., Singapore, Brazil, Ireland, and Frankfurt. The laws, regulations, and standards of the
        country in which your information is stored or processed may be different from those of your own country. You have understood that the
        risks under applicable data protection laws are different and we may transfer to and store your personal information at our overseas
        facilities. However, this does not change any of our commitments to safeguard your personal information in accordance with this
        Privacy Policy.
      </p>

      <h2 style={sectionTitle}>11. Contact Us</h2>
      <p>
        If you have questions or concerns about Flux, Flux Service, or privacy, please do not hesitate to contact our Data Protection Officer
        at <a href="mailto:appworks.ai@gmail.com" style={linkStyle}>appworks.ai@gmail.com</a>.
      </p>
      <p>
        Please provide: (i) your name (or account user name), your country/region of residence and your preferred method of contact; and (ii)
        the details of your request or comment along with any corresponding website links or pages.
      </p>

      <h2 style={sectionTitle}>12. Additional Terms</h2>
      <h3 style={subTitle}>US addendum</h3>
      <h3 style={subTitle}>California Consumer Privacy Act Notice</h3>
      <p>
        California residents have additional privacy rights under the California Consumer Privacy Act, which are described in the{' '}
        <a href="#/CCPANotice" style={linkStyle}>California Consumer Privacy Act Notice</a>.
      </p>

      <div style={{ height: 60 }} />
    </div>
  )
}

const sectionTitle = { fontSize: 20, fontWeight: 700, marginTop: 40, marginBottom: 12, color: '#111' }
const subTitle = { fontSize: 16, fontWeight: 600, marginTop: 24, marginBottom: 8, color: '#222' }
const listStyle = { paddingLeft: 24, marginBottom: 16 }
const linkStyle = { color: '#7c3aed', textDecoration: 'underline' }

export default PrivacyPolicy
